import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { courierApi } from '@/services/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Car, Bike, Loader2, Check } from 'lucide-react'

export default function VehiclePage() {
  const queryClient = useQueryClient()

  const { data: vehicle, isLoading } = useQuery({
    queryKey: ['vehicle'],
    queryFn: () => courierApi.getVehicle(),
  })

  const { data: vehicleTypes } = useQuery({
    queryKey: ['vehicle-types'],
    queryFn: () => courierApi.getVehicleTypes(),
  })

  const registerMutation = useMutation({
    mutationFn: (type: string) => courierApi.registerVehicle({ type }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vehicle'] }),
  })

  const updateMutation = useMutation({
    mutationFn: (type: string) => courierApi.updateVehicle({ type }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vehicle'] }),
  })

  const handleSelectType = (type: string) => {
    if (vehicle) {
      updateMutation.mutate(type)
    } else {
      registerMutation.mutate(type)
    }
  }

  const isPending = registerMutation.isPending || updateMutation.isPending

  // Default vehicle types if API doesn't return them
  const types = vehicleTypes?.length ? vehicleTypes : [
    { id: 'bicycle', name: 'Bicycle', icon: '🚲' },
    { id: 'scooter', name: 'Scooter', icon: '🛵' },
    { id: 'motorcycle', name: 'Motorcycle', icon: '🏍️' },
    { id: 'car', name: 'Car', icon: '🚗' },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Vehicle</h1>
        <p className="text-muted-foreground">Manage your delivery vehicle</p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Current Vehicle */}
          {vehicle && (
            <Card className="border-primary">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Check className="w-4 h-4 text-success" />
                  Current Vehicle
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center text-3xl">
                    {vehicle.type === 'car' ? <Car className="w-8 h-8" /> : 
                     vehicle.type === 'bicycle' ? <Bike className="w-8 h-8" /> : 
                     '🛵'}
                  </div>
                  <div>
                    <p className="font-semibold text-lg capitalize">{vehicle.type}</p>
                    {vehicle.make && vehicle.model && (
                      <p className="text-muted-foreground">{vehicle.make} {vehicle.model}</p>
                    )}
                    {vehicle.licensePlate && (
                      <p className="text-sm text-muted-foreground">{vehicle.licensePlate}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Vehicle Types */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {vehicle ? 'Change Vehicle Type' : 'Select Vehicle Type'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {types.map((type) => (
                  <Button
                    key={type.id}
                    variant={vehicle?.type === type.id ? 'default' : 'outline'}
                    className="h-auto py-4 flex flex-col gap-2"
                    onClick={() => handleSelectType(type.id)}
                    disabled={isPending}
                  >
                    <span className="text-2xl">{type.icon || '🚗'}</span>
                    <span>{type.name}</span>
                    {vehicle?.type === type.id && (
                      <Check className="w-4 h-4 absolute top-2 right-2" />
                    )}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {isPending && (
            <div className="fixed inset-0 bg-black/20 flex items-center justify-center">
              <Card className="p-6">
                <Loader2 className="w-8 h-8 animate-spin mx-auto" />
                <p className="mt-2 text-sm">Updating...</p>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  )
}
